<script lang="ts" setup>
import {
  type ConversationMessage,
  MessageMetadataType,
  type ScoreBlurbMetadata,
} from '@/core/conversations/message/conversation-message.type';
import MessageBubble from './MessageBubble.vue';
import CopilotBubble from './CopilotBubble.vue';
import MessageStatusChange from './MessageStatusChange.vue';
import MessageDoNotContactChange from './MessageDoNotContactChange.vue';
import MessageInterviewScheduling from './MessageInterviewScheduling.vue';
import { type JobApplicant, JobApplicantStatus } from '@factoryfixinc/ats-interfaces';
import { computed } from 'vue';
import DateUtils from '@/utils/date-utils';
import { findLastIndex } from '@/utils/arrays/lastIndex.util';

const props = defineProps<{
  messages: ConversationMessage[];
  currentProId: number | undefined;
  jobApplicant?: JobApplicant;
  aiRecruiterName: string;
  doNotContactHistoryUnavailable?: boolean;
}>();

type ScoreBlurbMessage = ConversationMessage & { metadata: ScoreBlurbMetadata };

// The slot only renders inside the SCORE_BLURB type guard below, so consumers get
// the narrowed metadata without re-checking the discriminant themselves.
defineSlots<{
  'score-blurb'?: (props: { message: ScoreBlurbMessage }) => unknown;
}>();

// Property narrowing on `message.metadata` does not retype `message` as a whole,
// so the guarded outlet needs this cast to satisfy the declared slot type.
function asScoreBlurbMessage(message: ConversationMessage): ScoreBlurbMessage {
  return message as ScoreBlurbMessage;
}

const INTERVIEW_SCHEDULING_TYPES = [
  MessageMetadataType.INTERVIEW_SCHEDULING_INITIATED,
  MessageMetadataType.INTERVIEW_SCHEDULED,
];

const lastRejectedStatusIndex = computed(() => {
  return findLastIndex(props.messages, 'content', JobApplicantStatus.REJECTED);
});

// Since we do not persist the text of the rejection reason in the status history
// this is a way arround to set the last `Rejected with reason` message but not
// the previous REJECTED ones
function isRejectedWithReason(index: number) {
  if (!props.jobApplicant) {
    return false;
  }

  return (
    index === lastRejectedStatusIndex.value &&
    props.jobApplicant.status === JobApplicantStatus.REJECTED &&
    props.jobApplicant.rejectionReason !== ''
  );
}

function showLineDivider(index: number) {
  if (index === 0) {
    return true;
  }

  return (
    props.messages[index].createdAt.getDate() !== props.messages[index - 1].createdAt.getDate()
  );
}

function isToday(date: Date) {
  return DateUtils.isSame(date);
}

function formatedDate(date: Date) {
  return isToday(date) ? 'Today' : DateUtils.formatTimestamp(date, 'dddd, MMMM Do');
}

function isInterviewSchedulingMessage(message: ConversationMessage) {
  return INTERVIEW_SCHEDULING_TYPES.includes(message.metadata?.type as MessageMetadataType);
}
</script>

<template>
  <div class="flex w-full flex-col flex-wrap justify-end p-2">
    <div v-for="(message, index) in messages" :key="message.key">
      <!-- Line Divider -->
      <div
        v-if="showLineDivider(index)"
        :class="{
          'line-divider': true,
          today: isToday(message.createdAt),
        }"
      >
        <span>{{ formatedDate(message.createdAt) }} </span>
      </div>

      <!-- Application Status Message -->
      <template v-if="message.metadata?.type === MessageMetadataType.APPLICATION_STATUS">
        <MessageStatusChange
          :message="message"
          :job-applicant="jobApplicant"
          :is-rejected-with-reason="isRejectedWithReason(index)"
        />
      </template>

      <!-- Interview Scheduling Events -->
      <MessageDoNotContactChange
        v-else-if="message.metadata?.type === MessageMetadataType.DO_NOT_CONTACT"
        :message="message"
      />

      <template v-else-if="isInterviewSchedulingMessage(message)">
        <MessageInterviewScheduling :message="message" />
      </template>

      <template v-else>
        <!-- Copilot score blurb. The optional scoped slot lets a caller (the mobile
             thread) swap in its own card; with no slot provided the desktop bubble
             renders unchanged. -->
        <template v-if="message.metadata?.type === MessageMetadataType.SCORE_BLURB">
          <slot name="score-blurb" :message="asScoreBlurbMessage(message)">
            <CopilotBubble :message="message" :ai-recruiter-name="aiRecruiterName" />
          </slot>
        </template>

        <!-- Employer, Pro and Bot Message -->
        <MessageBubble
          v-else
          :message="message"
          :current-pro-id="currentProId"
          :ai-recruiter-name="aiRecruiterName"
        />
      </template>
    </div>
    <p
      v-if="doNotContactHistoryUnavailable"
      role="status"
      data-test="dnc-history-unavailable"
      class="my-3 text-center text-sm text-shade-840"
    >
      Do Not Contact history unavailable
    </p>
  </div>
</template>
<style scoped lang="postcss">
.line-divider {
  @apply border-t border-tint-60 p-2 text-shade-600;
  @apply mb-2 mt-4 flex items-center justify-center;

  span {
    @apply border border-tint-60 bg-white px-3 py-1;
    border-radius: 16px;
    font-size: 10px;
    margin-top: -19px;
    line-height: 10px;
  }
}
.today {
  @apply border-highlight-500 border-highlight-500 font-bold text-highlight-500;

  span {
    @apply border-highlight-500;
    line-height: 10px;
  }
}
</style>
